import type { EventDto, TiptapDoc } from '@odo/shared-ui';
import { docFromPlainText, tiptapDocToPlainText, toEventAlignment } from '@odo/shared-ui';
import type { CreateEventInput, UpdateEventInput } from '../../services/events';
import { isoToLocalInput, localInputToIso } from '../../utils/eventDateTime';
import {
    ANNOUNCEMENT,
    ANNOUNCEMENT_DURATION_HOURS,
    ANNOUNCEMENT_THEME_PRESET,
    MEETING,
    isAnnouncementKind,
    toEventKind
} from './eventKind';

/** 'all' publishes to every practice (wire: `practices: []`); 'selected'
 *  limits it to `practices`, which validation then requires to be non-empty. */
export type AudienceMode = 'all' | 'selected';

// The form's working state. Timestamps are local `datetime-local` strings;
// UTC ISO conversion happens only at the edges (draftFromEvent / draftTo*Input).
export interface EventDraft {
    /** Read-only: derived from the title server-side and immutable after, since
     *  sign-ups are keyed by it. Present only so the preview has a full `EventDto`. */
    id: string;
    /** Read-only here (seeded from the event, never written by a control or sent
     *  by `draftToUpdateInput`) - just completes the preview's `EventDto`. */
    enabled: boolean;
    /** `EventKind`. Drives which fields the form authors and the server accepts. */
    kind: string;
    // Only these two instants and a duration are authored. `endsAt`/`showUntil`
    // are derived server-side (see events.rules.ts `resolveSchedule`).
    startsAtLocal: string;
    showFromLocal: string;
    /** Hours, as typed. Kept as a string so a half-typed "1." does not reset. */
    durationHours: string;
    title: string;
    topic: string;
    badgeLabel: string;
    badgeTag: string;
    subtitle: string;
    // Prepopulated from the banner (title/subtitle) so the author edits real
    // text; trimmed-blank still saves as null (see `modalFields`).
    modalTitle: string;
    modalSubtitleDoc: TiptapDoc | null;
    modalShowSubtitle: boolean;
    /** Plain-text flattening of `bodyDoc`, recomputed on every edit. */
    body: string;
    bodyDoc: TiptapDoc | null;
    /** Form-only, never sent: which audience the author picked. The wire has
     *  just `practices`, where empty means every practice - but that makes
     *  "narrowed to nobody yet" and "everyone" the same value, so an author who
     *  picked `selected` and added nothing would silently publish to everyone.
     *  Holding the intent separately lets `validateEventDraft` reject that. */
    audienceMode: AudienceMode;
    /** Practice ids this event is limited to. Meaningful only under
     *  `audienceMode: 'selected'`; `draftTo*Input` sends [] for 'all'. */
    practices: string[];
    themePreset: string;
    /** `EventAlignment`: banner layout, independent of `iconKey`. */
    alignment: string;
    // '' means "no icon" (iconKey: null); may be paired with either alignment.
    iconKey: string;
    actionLabel: string;
    countdownToEvent: boolean;
    // Write-only (spec §7) - always starts blank on edit, even though the
    // event already has one stored (see EventForm's Zoom join URL field).
    zoomUrl: string;
    googleCalendarUrl: string;
    icsUrl: string;
}

const MEETING_DEFAULT_DURATION_HOURS = '1';

/** Prefers the stored rich doc, then falls back to the banner subtitle - so
 *  the editor always opens on real, editable text. */
function seedModalSubtitleDoc(event: EventDto): TiptapDoc {
    const doc = (event.modalSubtitleDoc ?? null) as TiptapDoc | null;
    if (doc) return doc;
    return docFromPlainText(event.subtitle);
}

/** A stored event carries only the ids, so the mode is read back off them -
 *  the inverse of `audiencePractices`. */
function audienceModeOf(event: EventDto): AudienceMode {
    return (event.practices?.length ?? 0) > 0 ? 'selected' : 'all';
}

/** The wire value. 'all' sends [] whatever is parked in `practices`, so an
 *  author who narrows an event, then switches back to All, publishes to
 *  everyone rather than to whatever list was last on screen. */
function audiencePractices(draft: EventDraft): string[] {
    return draft.audienceMode === 'all' ? [] : draft.practices;
}

export function emptyEventDraft(defaultThemePreset: string): EventDraft {
    return {
        id: '',
        enabled: true,
        kind: MEETING,
        startsAtLocal: '',
        showFromLocal: '',
        durationHours: MEETING_DEFAULT_DURATION_HOURS,
        title: '',
        topic: '',
        badgeLabel: '',
        badgeTag: '',
        subtitle: '',
        modalTitle: '',
        modalSubtitleDoc: null,
        modalShowSubtitle: true,
        body: '',
        bodyDoc: null,
        // A new event is global unless the author narrows it.
        audienceMode: 'all',
        practices: [],
        themePreset: defaultThemePreset,
        alignment: 'left',
        iconKey: '',
        actionLabel: 'RSVP',
        countdownToEvent: true,
        zoomUrl: '',
        googleCalendarUrl: '',
        icsUrl: ''
    };
}

export function draftFromEvent(event: EventDto): EventDraft {
    return {
        id: event.id,
        enabled: event.enabled,
        kind: toEventKind(event.kind),
        startsAtLocal: isoToLocalInput(
            isAnnouncementKind(event.kind) ? event.showUntil : event.startsAt
        ),
        showFromLocal: isoToLocalInput(event.showFrom),
        // An announcement's stored duration is forced, not authored, so the
        // meeting default stands in should the admin convert it.
        durationHours: isAnnouncementKind(event.kind)
            ? MEETING_DEFAULT_DURATION_HOURS
            : String(event.durationHours ?? 1),
        title: event.title,
        topic: event.topic,
        badgeLabel: event.badgeLabel,
        badgeTag: event.badgeTag ?? '',
        subtitle: event.subtitle,
        modalTitle: event.modalTitle ?? event.title,
        modalSubtitleDoc: seedModalSubtitleDoc(event),
        modalShowSubtitle: event.modalShowSubtitle ?? true,
        body: event.body ?? '',
        bodyDoc: (event.bodyDoc ?? null) as TiptapDoc | null,
        audienceMode: audienceModeOf(event),
        practices: event.practices ?? [],
        themePreset: event.themePreset,
        alignment: toEventAlignment(event.alignment),
        iconKey: event.iconKey ?? '',
        actionLabel: event.actionLabel,
        countdownToEvent: event.countdownToEvent,
        zoomUrl: '',
        googleCalendarUrl: event.googleCalendarUrl ?? '',
        icsUrl: event.icsUrl ?? ''
    };
}

/** Seeds a new-event draft from an existing event's content. Schedule and
 *  links are per-run, not reusable, so they reset to `emptyEventDraft`'s blanks. */
export function draftFromEventForDuplicate(event: EventDto): EventDraft {
    return {
        ...emptyEventDraft(event.themePreset),
        kind: toEventKind(event.kind),
        title: event.title,
        topic: event.topic,
        badgeLabel: event.badgeLabel,
        badgeTag: event.badgeTag ?? '',
        subtitle: event.subtitle,
        modalTitle: event.modalTitle ?? event.title,
        modalSubtitleDoc: seedModalSubtitleDoc(event),
        modalShowSubtitle: event.modalShowSubtitle ?? true,
        body: event.body ?? '',
        bodyDoc: (event.bodyDoc ?? null) as TiptapDoc | null,
        // Carried like the rest of the content: a duplicate of a practice-
        // targeted event is almost always for the same audience.
        audienceMode: audienceModeOf(event),
        practices: event.practices ?? [],
        alignment: toEventAlignment(event.alignment),
        iconKey: event.iconKey ?? '',
        actionLabel: event.actionLabel,
        countdownToEvent: event.countdownToEvent
    };
}

/** Switching kind carries the fields that kind implies, so the form and the
 *  submitted payload can never disagree about what an announcement is. */
export function draftWithKind(
    draft: EventDraft,
    kind: string,
    defaultThemePreset: string
): EventDraft {
    if (kind !== ANNOUNCEMENT) return { ...draft, kind: MEETING };
    // Only seed the blue default over a theme the admin never chose.
    const themePreset =
        draft.themePreset === defaultThemePreset ? ANNOUNCEMENT_THEME_PRESET : draft.themePreset;
    return { ...draft, kind: ANNOUNCEMENT, themePreset };
}

/** Never sent over the wire. Blank/invalid timestamps fall back to "now" so a
 *  half-filled form still previews instead of crashing the shared renderers. */
export function draftToPreviewDto(draft: EventDraft): EventDto {
    const nowIso = new Date().toISOString();
    const startsAt = draftStartsAtIso(draft) || nowIso;
    // Mirrors the server's own derivation so the modal shows the real end time
    // while the admin is still typing.
    const hours = isAnnouncementKind(draft.kind)
        ? ANNOUNCEMENT_DURATION_HOURS
        : Number(draft.durationHours);
    const endsAt = Number.isFinite(hours)
        ? new Date(new Date(startsAt).getTime() + hours * 3600000).toISOString()
        : startsAt;
    return {
        id: draft.id || 'preview',
        enabled: draft.enabled,
        kind: draft.kind,
        startsAt,
        durationHours: Number.isFinite(hours) ? hours : 1,
        endsAt,
        showFrom: localInputToIso(draft.showFromLocal) ?? nowIso,
        showUntil: endsAt,
        title: draft.title || 'Event title',
        topic: draft.topic,
        badgeLabel: draft.badgeLabel || 'Badge',
        badgeTag: draft.badgeTag || null,
        subtitle: draft.subtitle || 'Subtitle goes here',
        modalTitle: draft.modalTitle || null,
        modalSubtitleDoc: nullIfBlankDoc(draft.modalSubtitleDoc),
        modalShowSubtitle: draft.modalShowSubtitle,
        body: draft.body || null,
        bodyDoc: nullIfBlankDoc(draft.bodyDoc),
        practices: audiencePractices(draft),
        themePreset: draft.themePreset,
        alignment: draft.alignment,
        iconKey: draft.iconKey || null,
        actionLabel: draft.actionLabel || 'RSVP',
        countdownToEvent: draft.countdownToEvent,
        googleCalendarUrl: draft.googleCalendarUrl || null,
        icsUrl: draft.icsUrl || null,
        signedUp: false
    };
}

/** The plain-text mirror of a document, recomputed on save (not stored as the
 *  admin types) so it can never disagree with the document, the source of truth. */
function docPlainText(doc: TiptapDoc | null, fallbackText: string): string | null {
    if (!doc) return fallbackText.trim() || null;
    return tiptapDocToPlainText(doc).trim() || null;
}

function bodyText(draft: EventDraft): string | null {
    return docPlainText(draft.bodyDoc, draft.body);
}

/** Null when the doc is absent or flattens to no visible text (e.g. only empty
 *  paragraphs) - an all-blank doc must not be stored as real content. */
function nullIfBlankDoc(doc: TiptapDoc | null): TiptapDoc | null {
    return doc && tiptapDocToPlainText(doc).trim() ? doc : null;
}

const MS_PER_HOUR = 60 * 60 * 1000;

// An announcement authors its banner end directly, but the server stores
// `showUntil` as `startsAt + durationHours` - shift so the two land on the minute.
export function draftStartsAtIso(draft: EventDraft): string {
    const iso = localInputToIso(draft.startsAtLocal) as string;
    if (!iso || !isAnnouncementKind(draft.kind)) return iso;
    return new Date(
        new Date(iso).getTime() - ANNOUNCEMENT_DURATION_HOURS * MS_PER_HOUR
    ).toISOString();
}

/** Assumes the draft already passed `validateEventDraft` - every ISO conversion below is non-null. */
export function draftToCreateInput(draft: EventDraft): CreateEventInput {
    return {
        // No `id` (server derives the slug). Always created enabled - the show
        // window controls visibility, and disabling is a deliberate list action.
        enabled: true,
        kind: toEventKind(draft.kind),
        startsAt: draftStartsAtIso(draft),
        showFrom: localInputToIso(draft.showFromLocal) as string,
        title: draft.title.trim(),
        badgeTag: draft.badgeTag.trim() || null,
        subtitle: draft.subtitle.trim(),
        ...modalFields(draft),
        body: bodyText(draft),
        bodyDoc: nullIfBlankDoc(draft.bodyDoc),
        practices: audiencePractices(draft),
        themePreset: draft.themePreset,
        alignment: toEventAlignment(draft.alignment),
        iconKey: draft.iconKey || null,
        ...kindFields(draft)
    };
}

type ModalFields = Pick<CreateEventInput, 'modalTitle' | 'modalSubtitleDoc' | 'modalShowSubtitle'>;

/** Blank overrides send `null` (inherit) - never `''`, which the server would
 *  reject as an empty string rather than treat as "unset". */
function modalFields(draft: EventDraft): ModalFields {
    return {
        modalTitle: draft.modalTitle.trim() || null,
        modalSubtitleDoc: nullIfBlankDoc(draft.modalSubtitleDoc),
        modalShowSubtitle: draft.modalShowSubtitle
    };
}

type KindFields = Pick<
    CreateEventInput,
    | 'durationHours'
    | 'topic'
    | 'badgeLabel'
    | 'actionLabel'
    | 'countdownToEvent'
    | 'zoomUrl'
    | 'googleCalendarUrl'
    | 'icsUrl'
>;

/** Every field a kind owns, in one place, so create and update stay in step.
 *  A meeting sends `zoomUrl` only when typed - blank keeps the stored link. */
function kindFields(draft: EventDraft): KindFields {
    if (isAnnouncementKind(draft.kind)) {
        return {
            durationHours: ANNOUNCEMENT_DURATION_HOURS,
            topic: '',
            badgeLabel: '',
            actionLabel: '',
            countdownToEvent: false,
            googleCalendarUrl: null,
            icsUrl: null
        };
    }
    const zoomUrl = draft.zoomUrl.trim();
    return {
        durationHours: Number(draft.durationHours),
        topic: draft.topic.trim(),
        badgeLabel: draft.badgeLabel.trim(),
        actionLabel: draft.actionLabel.trim(),
        countdownToEvent: draft.countdownToEvent,
        googleCalendarUrl: draft.googleCalendarUrl.trim() || null,
        icsUrl: draft.icsUrl.trim() || null,
        ...(zoomUrl ? { zoomUrl } : {})
    };
}

/** `enabled` is deliberately absent - the list's row menu is its sole writer. */
export function draftToUpdateInput(draft: EventDraft): UpdateEventInput {
    return {
        kind: toEventKind(draft.kind),
        startsAt: draftStartsAtIso(draft),
        showFrom: localInputToIso(draft.showFromLocal) as string,
        title: draft.title.trim(),
        badgeTag: draft.badgeTag.trim() || null,
        subtitle: draft.subtitle.trim(),
        ...modalFields(draft),
        body: bodyText(draft),
        bodyDoc: nullIfBlankDoc(draft.bodyDoc),
        practices: audiencePractices(draft),
        themePreset: draft.themePreset,
        alignment: toEventAlignment(draft.alignment),
        iconKey: draft.iconKey || null,
        ...kindFields(draft)
    };
}
